import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

// Types
export type AgencyTransferMode = "reparent" | "merge";
export type AgencyTransferJobStatus =
  | "queued"
  | "processing"
  | "completed"
  | "completed_with_errors"
  | "failed"
  | "canceled";

export interface AgencyTransferLogEntry {
  sourceAgencyId: string;
  sourceName?: string;
  status: "success" | "failed" | "skipped";
  agentsMoved?: number;
  leadsMoved?: number;
  subAgenciesReparented?: number;
  error?: string;
}

export interface AgencyTransferJob {
  id: string;
  mode: AgencyTransferMode;
  status: AgencyTransferJobStatus;
  totalAgencies: number;
  processedAgencies: number;
  progressMessage: string | null;
  log: AgencyTransferLogEntry[];
  resultSummary?: {
    total: number;
    succeeded: number;
    skipped: number;
    failed: number;
  } | null;
  errorMessage?: string | null;
  createdAt?: string;
  completedAt?: string | null;
}

export interface CreateTransferJobRequest {
  mode: AgencyTransferMode;
  targetAgencyId: string;
  sourceAgencyIds: string[];
}

export interface CreateTransferJobResponse {
  jobId: string;
  status: AgencyTransferJobStatus;
  totalAgencies: number;
}

// API Functions
export const createTransferJob = async (data: CreateTransferJobRequest) => {
  const url = getApiUrl("agencies/transfer/jobs");
  return handleApiCall<CreateTransferJobResponse>(async () => {
    return await api.post<CreateTransferJobResponse>({ url, body: data, auth: true });
  });
};

export const getTransferJob = async (jobId: string) => {
  const url = getApiUrl(`agencies/transfer/jobs/${jobId}`);
  return handleApiCall<{ job: AgencyTransferJob }>(async () => {
    return await api.get<{ job: AgencyTransferJob }>({ url, auth: true });
  });
};

export const listTransferJobs = async () => {
  const url = getApiUrl("agencies/transfer/jobs");
  return handleApiCall<AgencyTransferJob[]>(async () => {
    return await api.get<AgencyTransferJob[]>({ url, auth: true });
  });
};

export const cancelTransferJob = async (jobId: string) => {
  const url = getApiUrl(`agencies/transfer/jobs/${jobId}`);
  return handleApiCall<{ message: string }>(async () => {
    return await api.delete<{ message: string }>({ url, auth: true });
  });
};

// Retire an EMPTY sub-agency (no working agents) — folds it into its parent and
// soft-deletes it, so empty shells don't pile up after their agents move away.
export const retireAgency = async (agencyId: string) => {
  const url = getApiUrl(`agencies/${agencyId}/retire`);
  return handleApiCall<{ agentsMoved: number; leadsMoved: number; subAgenciesReparented: number }>(
    async () => {
      return await api.post({ url, body: {}, auth: true });
    },
  );
};
